import { motion, useScroll, useTransform } from "framer-motion";
import { CheckCircle2, Phone, Mail } from "lucide-react";
import { NeuralCanvas } from "../NeuralCanvas";
import { LeadForm } from "../LeadForm";
import { Eyebrow } from "../Reveal";
import { HERO_BENEFITS, SITE } from "../../lib/site";

const LINES = ["Get More Leads & Sales", "With Ads That", "Actually Convert."];

export const Hero = () => {
  const { scrollY } = useScroll();
  const yOrbA = useTransform(scrollY, [0, 700], [0, 130]);
  const yOrbB = useTransform(scrollY, [0, 700], [0, -90]);

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden pt-28 pb-16 lg:pt-32"
    >
      <NeuralCanvas className="absolute inset-0 w-full h-full opacity-60" />
      <motion.div
        style={{ y: yOrbA }}
        className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full bg-blue-600/20 blur-[140px] pointer-events-none"
      />
      <motion.div
        style={{ y: yOrbB }}
        className="absolute top-1/3 -right-48 w-[440px] h-[440px] rounded-full bg-violet-600/20 blur-[140px] pointer-events-none"
      />
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-10 items-center">
          <div className="lg:col-span-7 space-y-7">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Eyebrow>Performance Marketing Agency</Eyebrow>
            </motion.div>
            <h1
              data-testid="hero-headline"
              className="font-display text-4xl sm:text-5xl lg:text-[4rem] font-extrabold tracking-tight leading-[1.06]"
            >
              {LINES.map((line, i) => (
                <span className="mask-line" key={line}>
                  <motion.span
                    initial={{ y: "112%" }}
                    animate={{ y: 0 }}
                    transition={{ duration: 0.85, delay: 0.15 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
                    className={i === 2 ? "gradient-accent-text pb-1" : "text-white"}
                  >
                    {line}
                  </motion.span>
                </span>
              ))}
            </h1>
            <motion.p
              data-testid="hero-subheadline"
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.65 }}
              className="text-base sm:text-lg text-slate-300 leading-relaxed max-w-xl"
            >
              We turn your Meta &amp; Google ad budget into a predictable stream of
              ready-to-buy customers. No jargon. No vanity metrics. Just leads, sales
              and growth you can measure.
            </motion.p>
            <ul className="space-y-3">
              {HERO_BENEFITS.map((b, i) => (
                <motion.li
                  key={b}
                  data-testid={`hero-benefit-${i}`}
                  initial={{ opacity: 0, x: -18 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.8 + i * 0.12 }}
                  className="flex items-start gap-3 text-sm sm:text-base text-slate-200"
                >
                  <CheckCircle2 size={19} className="text-emerald-400 mt-0.5 shrink-0" />
                  {b}
                </motion.li>
              ))}
            </ul>
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 1.2 }}
              className="flex flex-wrap items-center gap-3 pt-2"
            >
              <a
                data-testid="hero-call-pill"
                href={`tel:${SITE.phoneRaw}`}
                className="inline-flex items-center gap-2 text-xs sm:text-sm font-medium text-slate-200 border border-white/15 rounded-full px-4 py-2 hover:border-cyan-400/50 hover:text-white transition-colors duration-200"
              >
                <Phone size={14} className="text-cyan-400" /> {SITE.phone}
              </a>
              <a
                data-testid="hero-email-pill"
                href={`mailto:${SITE.email}`}
                className="inline-flex items-center gap-2 text-xs sm:text-sm font-medium text-slate-200 border border-white/15 rounded-full px-4 py-2 hover:border-cyan-400/50 hover:text-white transition-colors duration-200"
              >
                <Mail size={14} className="text-cyan-400" /> {SITE.email}
              </a>
            </motion.div>
          </div>
          <div className="lg:col-span-5 relative">
            <motion.div
              initial={{ opacity: 0, y: 32, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.75, delay: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className="relative"
            >
              <div className="absolute -inset-px rounded-3xl bg-gradient-to-br from-blue-500/50 via-transparent to-violet-500/50 pointer-events-none" />
              <LeadForm formId="lead-form" prefix="hero" />
              <div
                data-testid="hero-floating-stat"
                className="hidden sm:flex absolute -left-8 -bottom-7 glass-card rounded-2xl px-4 py-3 items-center gap-3 animate-float-slow"
              >
                <span className="font-display text-2xl font-extrabold gradient-accent-text">64%</span>
                <span className="text-[11px] leading-tight text-slate-300">
                  lower cost
                  <br />
                  per lead
                </span>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};
